import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  ArrayParam,
  BooleanParam,
  NumericArrayParam,
  StringParam,
  useQueryParam,
  withDefault
} from 'use-query-params';

import { PaginatedList } from '../../common/PaginatedList.interface';
import {
  ApiResult,
  FetchApiCallback,
  useApiDelete,
  useApiGet,
  useApiPatch,
  useApiPost,
  useApiPut
} from '../../common/api/Api.hooks';
import api, { ApiParams, FetchError } from '../../common/api/api';
import { apiResolve } from '../../common/api/apiResolve';
import { VersionItem } from '../../common/version-item/Version.interface';
import { UserContext } from '../../user/User.context';
import { User } from '../../user/User.interface';
import { ChangeOrder } from '../changeOrder/ChangeOrder.interface';
import { TaskV2 } from '../task-v2/TaskV2.interface';
import { Task } from '../tasks/Task.interface';
import { TemplateType } from '../template/Template.interface';
import {
  ApprovedFileVersion,
  CreateFileDto,
  Document,
  DocumentStat,
  DocumentType,
  DocumentUploadState,
  FileDto,
  FileLogDto,
  FileStatus,
  FileVersionDto,
  MoveFileDto,
  PresignedUrl,
  UpdateFileVersionDto,
  UploadStatus
} from './Document.interface';
import { documents } from './Document.mocks';
import { DocumentFilterOptions } from './components/DocumentBoardFilter';

const isLoading = false;
const error = undefined;
const get = () => {};
let nextDocumentId = 1001;

function findFolder(
  folderDocumentId: number,
  folder = documents
): Document[] | undefined {
  for (const document of folder) {
    if (document.id === folderDocumentId && document.documents) {
      return document.documents;
    } else if (document.documents) {
      const foundFolder = findFolder(folderDocumentId, document.documents);
      if (foundFolder) {
        return foundFolder;
      }
    }
  }
}

function findDocument(
  documentId: number,
  folder = documents
): Document | undefined {
  for (const document of folder) {
    if (document.id === documentId) {
      return document;
    } else if (document.documents) {
      const foundFile = findDocument(documentId, document.documents);
      if (foundFile) {
        return foundFile;
      }
    }
  }
}

function getAllDocuments(
  folder: Document[],
  results: Document[] = []
): Document[] {
  for (const document of folder) {
    if (document.type === DocumentType.File) {
      results.push(document);
    } else if (document.documents) {
      results.push(...getAllDocuments(document.documents));
    }
  }
  return results;
}

export function getFileStatusLabel(status: FileStatus): string {
  switch (status) {
    case FileStatus.Draft:
      return 'Draft';
    case FileStatus.InReview:
      return 'In Review';
    case FileStatus.Approved:
      return 'Approved';
    case FileStatus.Obsolete:
      return 'Obsolete';
    default:
      return '';
  }
}

export function useFindDocuments(
  folderId?: number
): [ApiResult<FileDto>, FetchApiCallback] {
  const { data, isLoading, error, response, get } = useApiGet<
    FileDto | FileDto[]
  >(`folders/${folderId || ''}`);

  const result = useMemo(() => {
    if (folderId) return data as FileDto;
    return { id: 0, children: Array.isArray(data) ? data : data ? [data] : [] };
  }, [data, folderId]);

  return [{ data: result, isLoading, error, response }, get];
}

export function useSearchFiles(): [ApiResult<FileDto[]>, FetchApiCallback] {
  const { get, ...result } = useApiGet<FileDto[]>('files/search');
  return [result, get];
}

// TODO: remove mock document implementation
export function useFindBoardDocuments() {
  const boardDocuments = getAllDocuments(documents).sort((a, b) =>
    a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
  );
  const documentList = {
    total: boardDocuments.length,
    data: boardDocuments
  };
  return { documentList, isLoading, error, get };
}

export function useFindFileVersions(
  id: number
): [ApiResult<PaginatedList<VersionItem>>, FetchApiCallback] {
  const { get, ...result } = useApiGet<PaginatedList<VersionItem>>(
    `files/${id}/versions`
  );
  return [result, get];
}

export function useFindApprovedFiles(): [
  ApiResult<ApprovedFileVersion[]>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<ApprovedFileVersion[]>(
    'files/versions/approved'
  );
  return [result, get];
}

export function useFindFiles(): [
  ApiResult<[FileDto[], number]>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<[FileDto[], number]>('files');
  return [result, get];
}

export function useFindFilesPaginated(): [
  ApiResult<PaginatedList<FileDto>>,
  FetchApiCallback
] {
  const { get, ...result } =
    useApiGet<PaginatedList<FileDto>>('files/paginated');
  return [result, get];
}

export function useBoardDocuments(): [
  ApiResult<[Document[], number]>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<[Document[], number]>('files');
  return [result, get];
}

export function useUpdateBoardDocument(
  id?: number
): [ApiResult<Document> | null, FetchApiCallback | null] {
  const { patch, ...result } = useApiPatch<Document>(`files/${id}`);
  return typeof id !== 'number' ? [null, null] : [result, patch];
}

export function useFindDocument(
  id: string | number,
  version?: string | number
): [ApiResult<FileDto>, FetchApiCallback] {
  let url: string = `files/${id}`;
  url = typeof version !== 'undefined' ? `${url}?version=${version}` : url;
  const { data, isLoading, error, response, get } = useApiGet<FileDto>(url);
  return [{ data, isLoading, error, response }, get];
}

export function useFindDocumentChangeOrders(fileId: number) {
  const { data, isLoading, error, get } = useApiGet<ChangeOrder[]>(
    `files/${fileId}/change-orders`
  );
  return { changeOrders: data, isLoading, error, findChangeOrders: get };
}

export function useFindDocumentChangeOrdersV2(fileId: number) {
  const { data, get, ...result } = useApiGet<
    { id: number; name: string; taskV2: TaskV2; taskPath?: string }[]
  >(`files/${fileId}/change-orders-v2`);
  return { ...result, changeOrders: data, findChangeOrders: get };
}

export function useFindDocumentByTaskId(
  taskId: string | number,
  version?: string | number
): [ApiResult<FileDto>, FetchApiCallback] {
  let url: string = `files/task/${taskId}`;
  url = typeof version !== 'undefined' ? `${url}?version=${version}` : url;
  const { get, ...result } = useApiGet<FileDto>(url);
  return [result, get];
}

export function useGetFileTasks(): [ApiResult<Task[]>, FetchApiCallback] {
  const { get, ...result } = useApiGet<Task[]>('files/tasks');
  return [result, get];
}

export function useGetFolderByName(): [
  ApiResult<FileDto>,
  (name: string, parentPath?: string) => void
] {
  const { get, ...result } = useApiGet<FileDto>('folders/name');
  const getFolder = useCallback(
    (name: string, parentPath?: string) => {
      const params: ApiParams = { name };
      if (parentPath) {
        params.parentPath = parentPath;
      }
      get(params);
    },
    [get]
  );
  return [result, getFolder];
}

export function useGetFileTreeById(
  id: number
): [ApiResult<FileDto[]>, FetchApiCallback] {
  const { get, ...result } = useApiGet<FileDto[]>(`folders/tree/${id}`);
  return [result, get];
}

export interface CreateViewUrlProps {
  token?: string;
  isDocument?: boolean;
  getUrl: (token: string) => string;
}

export function createViewUrl(props: CreateViewUrlProps): URL {
  const { isDocument = false, getUrl } = props;
  const token: string = props.token || api.getAccessToken() || '';
  const proxyURL: URL = new URL(getUrl(token), api.getBaseUrl());
  if (isDocument) {
    const params: URLSearchParams = new URLSearchParams({
      src: proxyURL.toString()
    });
    return new URL(
      `https://view.officeapps.live.com/op/view.aspx?${params.toString()}`
    );
  }
  return proxyURL;
}

export function createProxyUrl(
  storageKey: string,
  token?: string,
  proxyOnly: boolean = false,
  name?: string
): URL {
  token = token || api.getAccessToken() || '';
  // name is the file name when being accessed or saved
  const namePath: string = name ? `/${encodeURI(name)}` : '';

  return createViewUrl({
    token,
    // use doc viewer if not viewing pdf file
    isDocument: !proxyOnly && !storageKey.includes('.pdf'),
    getUrl: (token: string) => {
      return `files/proxy${namePath}?storageKey=${storageKey}&token=${token}`;
    }
  });
}

export function useProxyUrl(
  storageKey: string,
  token?: string,
  proxyOnly: boolean = false,
  name?: string
): URL {
  return useMemo(() => {
    return createProxyUrl(storageKey, token, proxyOnly, name);
  }, [token, storageKey, proxyOnly, name]);
}

export function useDownloadAsPdf(
  storageKey: string
): [ApiResult<any>, Blob | undefined, () => void] {
  const [blob, setBlob] = useState<Blob | undefined>();
  const { get, ...result } = useApiGet('files/download-pdf');
  const { response } = result;

  const getDocument = useCallback(() => {
    // remove old document
    setBlob(undefined);
    get({ storageKey });
  }, [storageKey, get]);

  const setDocumentBlob = useCallback(async () => {
    if (!response?.ok) {
      return;
    }
    let blobToSave: Blob | undefined = blob;
    if (!blob) {
      blobToSave = await response.blob();
      setBlob(blobToSave);
    }
  }, [blob, response]);

  useEffect(() => {
    setDocumentBlob();
  }, [setDocumentBlob]);

  return [result, blob, getDocument];
}

export interface FileExportParams {
  type: TemplateType;
  version?: number;
  timeZone?: string;
  data?: Record<string, any>;
}

export function useExportFile(id: number, version?: number) {
  const [state, setState] = useState<{
    isLoading: boolean;
    error?: FetchError;
  }>({ isLoading: false });

  const exportFile = useCallback(
    (exportParams: FileExportParams) => {
      setState({ isLoading: true });
      const params = { ...exportParams };
      params.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (version) params.version = version;
      const request = api.get(`files/${id}/export`, params);
      const getBlob = async () => {
        const { response, error } = await apiResolve(request, {
          bodyParse: false
        });
        if (error || !response) {
          setState({ isLoading: false, error });
          return;
        }
        const blob = await response.blob();
        setState({ isLoading: false });
        return blob;
      };
      return { request, result: getBlob() };
    },
    [id, version]
  );

  return { ...state, exportFile };
}

export function useCreateDocumentFolder(): [
  ApiResult<FileDto>,
  FetchApiCallback
] {
  const { data, isLoading, response, error, post } =
    useApiPost<FileDto>(`folders`);
  return [{ data, isLoading, response, error }, post];
}

export function useGetPresignedUrl(): [
  ApiResult<PresignedUrl>,
  (mimeType: string) => void
] {
  const { data, response, isLoading, error, get } =
    useApiGet<PresignedUrl>('files/presign');
  const getPresignedUrl = useCallback(
    (mimeType: string) => get({ folderPath: 'documents', mimeType }),
    [get]
  );
  return [{ data, response, isLoading, error }, getPresignedUrl];
}

type UploadAction = {
  type: UploadStatus;
  payload?: any;
};
function uploadReducer(
  state: DocumentUploadState,
  action: UploadAction
): DocumentUploadState {
  switch (action.type) {
    case 'init':
      return {
        ...state,
        status: action.type,
        file: action.payload,
        progress: 0,
        error: undefined
      };
    case 'ready':
      return {
        ...state,
        status: action.type,
        presignedUrl: action.payload as PresignedUrl,
        progress: 1
      };
    case 'uploading':
      return {
        ...state,
        status: action.type,
        progress: action.payload as number
      };
    case 'error':
      return {
        ...state,
        status: action.type,
        error: action.payload
      };
    case 'complete':
      return {
        ...state,
        status: action.type,
        progress: 100,
        response: action.payload as Response
      };
    case 'cancel':
      return {
        ...state,
        status: action.type,
        file: undefined,
        progress: 0,
        error: undefined
      };
    default:
      return { ...state, status: action.type };
  }
}
type UploadCallback = (command: 'upload' | 'cancel', file?: File) => void;

export function useUploadDocument(): [DocumentUploadState, UploadCallback] {
  const [presignedState, getPresignedUrl] = useGetPresignedUrl();
  const requestRef = useRef(new XMLHttpRequest());

  const [{ file, presignedUrl, response, status, progress, error }, dispatch] =
    useReducer<typeof uploadReducer>(uploadReducer, { progress: 0 });

  useEffect(() => {
    const request = requestRef.current;

    if (status === 'init' && file) {
      getPresignedUrl(file.type);
      dispatch({ type: 'presign' });
    } else if (
      status === 'presign' &&
      !presignedState.isLoading &&
      presignedState.data
    ) {
      dispatch({ type: 'ready', payload: presignedState.data });
    } else if (status === 'ready' && presignedUrl && file) {
      request.addEventListener('error', e =>
        dispatch({ type: 'error', payload: e })
      );
      request.addEventListener('timeout', e =>
        dispatch({ type: 'error', payload: e })
      );
      request.upload.addEventListener('error', e =>
        dispatch({ type: 'error', payload: e })
      );
      request.upload.addEventListener('progress', e => {
        if (e.lengthComputable) {
          dispatch({ type: 'uploading', payload: (e.loaded / e.total) * 100 });
        }
      });
      request.upload.addEventListener('load', e => {
        dispatch({ type: 'complete', payload: request.response });
      });
      request.open('PUT', presignedUrl.signedUrl);
      // request.timeout = 5000;
      request.setRequestHeader('Content-Type', file.type);
      request.responseType = 'text';
      request.send(file);
      dispatch({ type: 'start' });
    } else if (status === 'cancel') {
      request.abort();
    }

    return () => {
      // clean up
      if (status === 'error' || status === 'complete') {
        request.abort();
      }
    };
  }, [getPresignedUrl, presignedState, file, presignedUrl, status, error]);

  const callback = useCallback(
    (command: 'upload' | 'cancel', fileItem?: File) => {
      if (command === 'upload' && fileItem) {
        dispatch({ type: 'init', payload: fileItem });
      }
      if (command === 'cancel') {
        dispatch({ type: 'cancel' });
      }
    },
    [dispatch]
  );

  return [{ file, presignedUrl, status, progress, response, error }, callback];
}

export function useCreateFileRecord(): [
  ApiResult<FileDto>,
  (file: CreateFileDto) => void
] {
  const [file, setFile] = useState<FileDto | undefined>();
  const { data, isLoading, response, error, post } =
    useApiPost<FileDto>('files');
  useEffect(() => {
    setFile(data);
  }, [data, error]);
  const createFileRecord = useCallback(
    (file: CreateFileDto) => post(file),
    [post]
  );
  return [{ data: file, isLoading, response, error }, createFileRecord];
}

export function useGetFileVersion(
  id: number,
  version: number
): [ApiResult<FileVersionDto>, FetchApiCallback] {
  const { get, ...result } = useApiGet<FileVersionDto>(
    `files/${id}/version/${version}`
  );
  return [result, get];
}

export function useUpdateFileVersion(
  id: number,
  version: number
): [ApiResult<FileDto>, (fileVersion: UpdateFileVersionDto) => void] {
  const { data, isLoading, error, response, put } = useApiPut<FileDto>(
    `files/${id}/version/${version}`
  );
  const updateFileVersion = useCallback(
    (fileVersion: UpdateFileVersionDto) => put(fileVersion),
    [put]
  );
  return [{ data, isLoading, error, response }, updateFileVersion];
}

export function useRevertFileVersion(
  id: number
): [ApiResult<FileDto>, FetchApiCallback] {
  const { data, isLoading, error, response, del } = useApiDelete<FileDto>(
    `files/${id}/revert`
  );
  return [{ data, isLoading, error, response }, del];
}

export function useCreateDocument(folderDocumentId?: number) {
  const [document, setDocument] = useState<Document>();
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useContext(UserContext);
  const post = useCallback(
    (documentDto: Partial<Document>) => {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        let folder = documents;
        if (folderDocumentId) {
          folder = findFolder(folderDocumentId) || documents;
        }
        documentDto.id = nextDocumentId;
        nextDocumentId++;
        documentDto.type = DocumentType.File;
        if (user) {
          documentDto.modifiedBy = user;
        }
        documentDto.version = 1;
        if (documentDto.fileVersion) {
          documentDto.fileVersion.status = FileStatus.Draft;
        }
        documentDto.modified = new Date();
        documentDto.history = [
          {
            version: 1,
            status: FileStatus.Draft,
            created: new Date(),
            createdBy: `${user?.firstName} ${user?.lastName}`
          }
        ];
        folder.push(documentDto as Document);
        setDocument(documentDto as Document);
      }, 300);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [folderDocumentId]
  );
  return { document, isLoading, error: undefined, post };
}

export function useUpdateDocument(id: number) {
  const [document, setDocument] = useState<Document>();
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const patch = useCallback(
    (documentDelta: Partial<Document>) => {
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        const foundDocument = findDocument(id);
        if (foundDocument) {
          Object.assign(foundDocument, documentDelta);
          foundDocument.history?.unshift({
            version: foundDocument.version || 1,
            status: foundDocument.fileVersion?.status || FileStatus.Draft,
            created: new Date(),
            createdBy: `${user?.firstName} ${user?.lastName}`
          });
          setDocument(foundDocument);
        }
      }, 300);
    },
    [id, user]
  );
  return { document, isLoading, error, patch };
}

export function useFindFolders(
  isFolderOnly: boolean = true
): [ApiResult<FileDto | FileDto[]>, (folderId?: number) => void] {
  const { get: apiGet, ...result } = useApiGet<FileDto | FileDto[]>('folders');
  const get = useCallback(
    (folderId?: number) => {
      const props = { isFolderOnly };
      if (folderId) {
        Object.assign(props, {
          id: folderId,
          withChildren: true,
          withParent: false
        });
      }

      apiGet(props);
    },
    [apiGet, isFolderOnly]
  );

  return [result, get];
}

export function useMoveFile(): [
  ApiResult<FileDto | FileDto[]>,
  (dto: MoveFileDto) => void
] {
  const { put: apiPut, ...result } = useApiPut<FileDto>('files/move');
  const put = useCallback((dto: MoveFileDto) => apiPut(dto), [apiPut]);
  return [result, put];
}

export function useUpdateFile(
  id: number,
  isFolder: boolean = false
): [ApiResult<FileDto>, (dto: Partial<FileDto> & ApiParams) => void] {
  const path = isFolder ? 'folders' : 'files';
  const { data, isLoading, error, patch } = useApiPatch<FileDto>(
    `${path}/${id}`
  );
  const updateFile = useCallback(
    (dto: Partial<FileDto>) => {
      patch(dto);
    },
    [patch]
  );
  return [{ data, isLoading, error }, updateFile];
}

export function useDeleteDocument(id: number) {
  const { data, isLoading, error, del } = useApiDelete<FileDto>('files/' + id);
  return { data, isLoading, error, del };
}

export function useGetDocumentHistory(id: number) {
  return useApiGet<PaginatedList<FileLogDto>>('files/history/' + id);
}
// A custom hook that builds on useLocation to parse
// the query string for you.
export function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function useGetDocumentStats(): [
  ApiResult<DocumentStat[]>,
  FetchApiCallback
] {
  const { get, ...result } = useApiGet<DocumentStat[]>('files/stats');
  return [result, get];
}

export function useDocumentUploadData(document: FileDto | undefined) {
  const uploadedBy: User | undefined =
    document?.fileVersion?.uploadedBy ||
    document?.fileVersion?.createdBy ||
    document?.uploadedBy ||
    document?.createdBy;
  const uploadDate: Date | undefined =
    document?.fileVersion?.uploaded ||
    document?.fileVersion?.created ||
    document?.uploaded ||
    document?.modified ||
    document?.created;
  const uploaderName: string = uploadedBy
    ? `${uploadedBy.firstName} ${uploadedBy.lastName}`
    : '?';
  const approvedDate =
    document?.task?.versionLog?.created ||
    document?.fileVersion?.task?.approvedDate;
  return { uploadedBy, uploadDate, uploaderName, approvedDate };
}

export function useDocumentFilterQueryParams() {
  const [assigneeId, setAssigneeId] = useQueryParam(
    'assigneeId',
    withDefault<number[], number[]>(NumericArrayParam as any, [])
  );
  const [type, setType] = useQueryParam(
    'type',
    withDefault<string[], string[]>(ArrayParam as any, [])
  );
  const [status, setStatus] = useQueryParam(
    'status',
    withDefault<string[], string[]>(ArrayParam as any, [])
  );
  const [dueDateFrom, setDueDateFrom] = useQueryParam(
    'dueDateFrom',
    withDefault(StringParam, undefined)
  );
  const [dueDateTo, setDueDateTo] = useQueryParam(
    'dueDateTo',
    withDefault(StringParam, undefined)
  );
  const [assignedToMe, setAssignedToMe] = useQueryParam(
    'assignedToMe',
    withDefault(BooleanParam, undefined)
  );

  // only set properties that have values
  const filterOptions = useCallback((options: DocumentFilterOptions) => {
    const { assigneeId, type, status, dueDateFrom, dueDateTo, assignedToMe } =
      options;
    const opts: Partial<DocumentFilterOptions> = {};
    if (assigneeId && assigneeId.length > 0) {
      opts.assigneeId = assigneeId;
    }
    if (status && status.length > 0) {
      opts.status = status;
    }
    if (type && type.length > 0) {
      opts.type = type;
    }
    if (!!dueDateFrom) {
      opts.dueDateFrom = dueDateFrom;
    }
    if (!!dueDateTo) {
      opts.dueDateTo = dueDateTo;
    }
    if (typeof assignedToMe === 'boolean') {
      opts.assignedToMe = assignedToMe;
    }
    // return only if at least one property is available
    return Object.keys(opts).length > 0 ? opts : undefined;
  }, []);

  const options = useMemo(() => {
    return filterOptions({
      assigneeId,
      type,
      status,
      dueDateFrom,
      dueDateTo,
      assignedToMe
    });
  }, [
    assigneeId,
    type,
    status,
    dueDateFrom,
    dueDateTo,
    assignedToMe,
    filterOptions
  ]);

  const update = useCallback(
    (options: Partial<DocumentFilterOptions>) => {
      setAssigneeId(options.assigneeId || []);
      setType(options.type || []);
      setStatus(options.status || []);
      setDueDateFrom(options.dueDateFrom);
      setDueDateTo(options.dueDateTo);
      setAssignedToMe(options.assignedToMe);
    },
    [
      setAssignedToMe,
      setAssigneeId,
      setStatus,
      setDueDateFrom,
      setDueDateTo,
      setType
    ]
  );

  return { options, update, filterOptions };
}
